// DAS-2777: costs per portfolio component, by type — below the Fair comparison.
// Shows what the data sources actually carry. A figure we do NOT have is rendered as
// "n/a" with the reason on hover, never as €0,00 — a zero would claim you paid nothing.
import React, { useState, useEffect } from 'react';
import api from '../functions/api';

const eur = (v) => (v == null ? null : '€' + v.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// A missing value is not a zero: show it as unknown, with the reason on hover.
const Amount = ({ value, note }) => {
  if (value == null) {
    return (
      <span style={{ color: '#bbb', fontStyle: 'italic' }}
            title={note || 'not available in the data sources'}>n/a</span>
    );
  }
  return <>{eur(value)}</>;
};

// DAS-2791: a fund's ongoing charge is taken INSIDE the fund, so it shows up in no
// booking at any broker. Three distinct states, and they must not be confused:
//   holds no funds        -> "—"   (nothing to pay; true today for DeGiro)
//   holds funds, % known  -> the amount
//   holds funds, % unknown-> "n/a" + how much value sits behind the gap
const FundCost = ({ fc }) => {
  if (!fc || !fc.holdings) return <>—</>;
  const known = fc.total > 0;
  const gap = fc.unknown_value_eur;
  if (!known) {
    return (
      <span style={{ color: '#bbb', fontStyle: 'italic' }}
            title={`${fc.unknown.length} of ${fc.holdings} funds have no published charge on file yet` +
                   (gap ? ` — ${eur(gap)} of position value behind the gap` : '')}>n/a</span>
    );
  }
  return (
    <>{eur(fc.total)}
      {fc.unknown?.length ? (
        <><br /><span style={{ color: '#c60', fontSize: 11 }}
              title={`${fc.unknown.length} fund(s) still missing a published charge` +
                     (gap ? ` — ${eur(gap)} of value` : '')}>
          +{fc.unknown.length} unknown</span></>
      ) : null}
    </>
  );
};

const CostsSection = () => {
  const [parts, setParts] = useState(null);

  useEffect(() => {
    api.get('/portfolio_costs')
      .then(res => setParts(res.data?.parts || null))
      .catch(() => setParts(null));
  }, []);

  if (!parts) return null;
  const cell = { padding: '2px 12px', textAlign: 'right', whiteSpace: 'nowrap' };
  const head = { ...cell, color: '#888', fontWeight: 500 };

  // Two totals, because these are two different things and adding them is meaningless:
  // the broker columns are costs ALREADY BOOKED this year, the fund column is a yearly
  // RATE that is accruing inside the funds. W&O has no booked costs at all — its whole
  // cost is the fund rate — so it belongs in the fund total, not the booked total.
  const num = (v) => (v == null ? 0 : Number(v));
  const bookedYTD = parts.reduce(
    (sum, p) => sum + (p.available && !p.is_yearly_fund_cost ? num(p.total) : 0), 0);
  const fundYearly = parts.reduce(
    (sum, p) => sum + (!p.available ? 0
      : p.is_yearly_fund_cost ? num(p.management_fee_yearly)
      : num(p.fund_costs?.total)), 0);

  // "at least" applies per total: an unknown booked figure does not make the fund total
  // incomplete, and a fund with no published charge does not make the booked total incomplete.
  const bookedIncomplete = parts.some(
    (p) => !p.available || (p.available && !p.is_yearly_fund_cost && p.currency_costs == null));
  const fundIncomplete = parts.some(
    (p) => p.available && !p.is_yearly_fund_cost && p.fund_costs?.unknown?.length > 0);
  const atLeast = (flag) => (flag
    ? <span style={{ color: '#888', fontWeight: 400, fontSize: 11 }}> at least</span> : null);

  return (
    <div data-testid="costs-section" style={{ margin: '16px 0 4px', fontSize: 12, overflowX: 'auto' }}>
      <span style={{ fontSize: 13 }}>
        <strong>Costs</strong>
        <span style={{ color: '#888', marginLeft: 8 }}>
          per component · booked costs are year-to-date, fund costs are a yearly rate
        </span>
      </span>
      <table style={{ borderCollapse: 'collapse', marginTop: 4 }}>
        <thead>
          <tr>
            <th style={{ ...head, textAlign: 'left' }}>Part</th>
            <th style={head}>Transaction costs</th>
            <th style={head}>Currency costs</th>
            <th style={head}>Other</th>
            <th style={head}>Fund costs / yr</th>
            <th style={head}>Total booked YTD</th>
            <th style={{ ...head, textAlign: 'left' }}></th>
          </tr>
        </thead>
        <tbody>
          {parts.map((p) => (
            <tr
              key={p.part}
              title={p.is_yearly_fund_cost
                ? `${p.note}\n` + (p.funds || []).map(f => `${f.fund}: ${f.fee_pct}% (${f.mgmt_pct}% mgmt + ${f.other_pct}% other) of €${Math.round(f.value_eur).toLocaleString('nl-NL')} = €${Math.round(f.yearly_cost_eur)}/yr`).join('\n')
                : (p.note || '')}
              style={{ cursor: 'help' }}
            >
              <td style={{ ...cell, textAlign: 'left', fontWeight: 600 }}>{p.part}</td>
              {p.available && p.is_yearly_fund_cost ? (
                /* DAS-2784: W&O — the funds' own published yearly costs, charged inside
                   the fund, so never mixed into the booked-cost columns */
                <>
                  <td style={cell}><Amount value={null} note="charged inside the fund, not per transaction" /></td>
                  <td style={cell}><Amount value={null} note="charged inside the fund, not per transaction" /></td>
                  <td style={cell}>—</td>
                  <td style={cell}>{eur(p.management_fee_yearly)}</td>
                  <td style={cell}>
                    <Amount value={null} note="nothing is booked for W&O — the whole cost is the funds' yearly charge, shown left" />
                  </td>
                  <td style={{ ...cell, textAlign: 'left', color: '#888' }}>
                    {(p.funds || []).length} funds · published %
                  </td>
                </>
              ) : p.available ? (
                <>
                  <td style={cell}><Amount value={p.transaction_costs} note={p.transaction_note} /></td>
                  <td style={cell}><Amount value={p.currency_costs} note={p.currency_note} /></td>
                  <td style={cell}>
                    {((p.service_costs || 0) + (p.other_costs || 0)) > 0 ? (
                      <>{eur((p.service_costs || 0) + (p.other_costs || 0))}<br />
                        <span style={{ color: '#888', fontSize: 11 }}>
                          {/* labels come from the API: each broker's "other" bucket is a
                              different real thing (DeGiro ADR/GDR vs Saxo ongoing charges) */}
                          {[p.service_costs > 0 ? (p.service_label || 'service') : null,
                            p.other_costs > 0 ? (p.other_label || 'other') : null]
                            .filter(Boolean).join(' + ')}
                        </span></>
                    ) : '—'}
                  </td>
                  <td style={cell}><FundCost fc={p.fund_costs} /></td>
                  <td style={{ ...cell, fontWeight: 600 }}>{eur(p.total)}</td>
                  <td style={{ ...cell, textAlign: 'left', color: '#888' }}>
                    {/* what is counted differs per component: DeGiro has a trade feed,
                        the others only have cost lines — so the API names its own unit */}
                    {p.count ?? p.trades} {p.count_label || 'trades'}
                  </td>
                </>
              ) : (
                <td colSpan={6} style={{ ...cell, textAlign: 'left', color: '#aaa', fontStyle: 'italic' }}>
                  {p.note}
                </td>
              )}
            </tr>
          ))}
          <tr data-testid="costs-total-row" style={{ borderTop: '2px solid #ddd', fontWeight: 700 }}>
            <td style={{ ...cell, textAlign: 'left' }}>Total</td>
            <td style={cell} />
            <td style={cell} />
            <td style={cell} />
            <td style={cell} data-testid="costs-total-fund">{eur(fundYearly)}{atLeast(fundIncomplete)}</td>
            <td style={cell} data-testid="costs-total-booked">{eur(bookedYTD)}{atLeast(bookedIncomplete)}</td>
            <td style={{ ...cell, textAlign: 'left', color: '#888', fontWeight: 400 }}>
              booked this year, plus the yearly fund rate
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default CostsSection;
